import { zodResolver } from '@hookform/resolvers/zod';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SelectableButton from '@ui/atoms/SelectableButton';
import SelectableButtonGroup from '@ui/atoms/SelectableButtonGroup';

import BackAndNextNav, { IBackAndNextNavProps } from '@ui/molecules/BackAndNextNav';
import DetailText from '@ui/molecules/DetailText';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { commonLabels } from '@utils/button';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import { dataConstants, schema } from './MembershipInsuranceCoverageStep.data';

export interface IMembershipInsuranceCoverageStepPageContent {
	pageHeader: string;
	pageInfo: string;
	checkInsuranceDetail: {
		title: string;
		description: string;
	};
	noInsuranceDetail: {
		title: string;
		description: string;
	};
	unsureInsuranceDetail: {
		title: string;
		description: string;
	};
	governmentInsuranceDetail: {
		title: string;
		description: string;
	};
}

interface IMembershipInsuranceCoverageStepProps extends IFlowStepComponentProps {
	pageContent: IMembershipInsuranceCoverageStepPageContent;
}

const FullWidthForm = fullWidth('form');
const FullWidthColumn = fullWidth(FlexColumn);
const FullWidthNavigation = fullWidth<IBackAndNextNavProps>(BackAndNextNav);
const SpacedHeading = spaced(HeadingText);
const SpacedText = spaced(BasicText);

const MembershipInsuranceCoverageStep: FC<IMembershipInsuranceCoverageStepProps> = ({
	pageContent,
	existingFormData,
	handleFormSubmission = () => {},
	handleNavBack = () => {},
	stepKey,
}) => {
	const [submitting, setSubmitting] = useState(false);

	const {
		pageHeader,
		pageInfo,
		checkInsuranceDetail,
		noInsuranceDetail,
		unsureInsuranceDetail,
		governmentInsuranceDetail,
	} = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const { handleSubmit, control } = form;

	const onSubmit = (data: Record<string, unknown>) => {
		setSubmitting(true);
		handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<FullWidthColumn>
			<SpacedHeading marginBottom="sm">{pageHeader}</SpacedHeading>
			<SpacedText marginBottom="lg">{pageInfo}</SpacedText>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)}>
				<Controller
					name="insuranceCoverage"
					control={control}
					render={(args) => {
						const field = {
							...args.field,
							value: args.field.value ?? '',
						};
						const errors = args.formState.errors;
						return (
							<SelectableButtonGroup
								layout="column"
								gap="sm"
								error={!!errors?.insuranceCoverage}
								errorLabel={errors?.insuranceCoverage?.message}>
								<SelectableButton
									optionValue={dataConstants.check}
									optionLabel={checkInsuranceDetail.title}
									alignment="start"
									error={errors?.insuranceCoverage?.message}
									{...field}>
									<DetailText
										gap="xxs"
										title={checkInsuranceDetail.title}
										description={checkInsuranceDetail.description}
									/>
								</SelectableButton>
								<SelectableButton
									optionValue={dataConstants.none}
									optionLabel={noInsuranceDetail.title}
									alignment="start"
									error={errors?.insuranceCoverage?.message}
									{...field}>
									<DetailText
										gap="xxs"
										title={noInsuranceDetail.title}
										description={noInsuranceDetail.description}
									/>
								</SelectableButton>
								<SelectableButton
									optionValue={dataConstants.unsure}
									optionLabel={unsureInsuranceDetail.title}
									alignment="start"
									error={errors?.insuranceCoverage?.message}
									{...field}>
									<DetailText
										gap="xxs"
										title={unsureInsuranceDetail.title}
										description={unsureInsuranceDetail.description}
									/>
								</SelectableButton>
								<SelectableButton
									disabled
									optionValue={dataConstants.government}
									optionLabel={governmentInsuranceDetail.title}
									alignment="start"
									{...field}>
									<DetailText
										gap="xxs"
										title={governmentInsuranceDetail.title}
										description={governmentInsuranceDetail.description}
									/>
								</SelectableButton>
							</SelectableButtonGroup>
						);
					}}
				/>
				<FullWidthNavigation
					marginTop="lgxl"
					backOnClick={() => handleNavBack(stepKey)}
					submitLabel={commonLabels.next}
					submitting={submitting}
				/>
			</FullWidthForm>
		</FullWidthColumn>
	);
};

export default MembershipInsuranceCoverageStep;
